import React, { useState } from "react";
import { DateTime } from "luxon";
import styles from "../FinanceDetailV2.module.scss";
import EcheancierRecapModal from "./EcheancierRecapModal";
import EcheancierUploader from "./EcheancierUploader";

const formatDate = (d) => {
  if (!d) return "";
  try {
    return DateTime.fromJSDate(new Date(d)).toFormat("yyyy-MM-dd");
  } catch {
    return d;
  }
};

// LS-670 — origine du taux VR effectif, affichée à côté du taux (ex : « 55,00 % (devis) »).
const ORIGINE_TAUX_LABELS = {
  devis: "devis",
  grille: "grille",
  saisi: "saisi",
  defaut: "défaut",
};

// Fraction API (0-1) → pourcentage affiché/édité (ex : 0.55 → "55"), arrondi à 2 décimales.
const formatTauxPourcentage = (fraction) =>
  fraction != null ? String(Math.round(fraction * 10000) / 100) : "";

const FinanceTabV2 = ({
  quotation,
  quotationIndex,
  isCalculating,
  disabled,
  // LS-612 — indépendant de `disabled` (verrouillage classique dès Dossier
  // complet) : saisie possible dès Accord reçu, y compris au-delà.
  canSaisirEcheancier,
  // Nombre de périodes de ce devis tel que persisté côté serveur (dernier
  // chargement/enregistrement) — undefined tant que non chargé.
  savedPeriodsCount,
  onFieldChange,
  // LS-683 — signale la sortie (blur) d'un champ VR : le hook déclenche alors
  // le recalcul de la saisie en attente, au lieu de recalculer pendant la frappe.
  onFieldCommit,
  onAddPeriod,
  onRemovePeriod,
  onSaisirMensualiteRetenue,
  // LS-634 — dépôt du calendrier des loyers (Excel source), propre à ce devis.
  demandeUuid,
  documentUploadDisabled,
  onDocumentUploaded,
  // LS-638 — tous les devis de la demande, pour la sélection multi-devis.
  devisOptions,
  // LS-638 — types de documents rattachés à un devis, à agréger dans la liste.
  devisLinkedTypeOptions,
  // ST1-2069 — incrémenté par le parent quand la section "Documents" a déposé
  // ou supprimé un document : recharge la liste de ce devis.
  documentsReloadKey,
}) => {
  const handleChange = (periodIndex, e) => {
    onFieldChange(quotationIndex, periodIndex, e.target.name, e.target.value);
  };

  // ── Échéancier (LS-612) ─────────────────────────────────────────
  const [echeancierEnEdition, setEcheancierEnEdition] = useState(false);
  const [echeancierBuffer, setEcheancierBuffer] = useState({});
  const [isSavingEcheancier, setIsSavingEcheancier] = useState(false);
  const [showEcheancierRecap, setShowEcheancierRecap] = useState(false);

  const hasEcheancierValues = quotation.periods.some(
    (p) => p.monthPaymentReal != null,
  );
  // LS-724 — les paliers déjà saisis restent consultables par tout profil
  // (lecture seule) ; seule la saisie dépend de canSaisirEcheancier.
  const showEcheancierColumns = hasEcheancierValues || echeancierEnEdition;

  // Ajout/suppression de période pas encore enregistré (le bouton principal
  // « Enregistrer » n'a pas encore été cliqué) : la saisie de l'échéancier
  // interrogerait une période inexistante côté serveur et échouerait (422).
  const hasUnsavedPeriodChange =
    savedPeriodsCount != null && quotation.periods.length !== savedPeriodsCount;

  const startEcheancierEdition = () => {
    const buffer = {};
    quotation.periods.forEach((p) => {
      buffer[p.num] = {
        mensualiteRetenue: p.monthPaymentReal ?? "",
        dateDebut: formatDate(p.dBegin),
      };
    });
    setEcheancierBuffer(buffer);
    setEcheancierEnEdition(true);
  };

  const cancelEcheancierEdition = () => {
    setEcheancierEnEdition(false);
    setEcheancierBuffer({});
  };

  const handleEcheancierChange = (numero, field, value) => {
    setEcheancierBuffer((prev) => ({
      ...prev,
      [numero]: { ...prev[numero], [field]: value },
    }));
  };

  const saveEcheancier = async () => {
    setIsSavingEcheancier(true);
    try {
      for (const period of quotation.periods) {
        const buffered = echeancierBuffer[period.num];
        if (!buffered || buffered.mensualiteRetenue === "") continue;

        const mensualiteChanged =
          period.monthPaymentReal == null ||
          Number(buffered.mensualiteRetenue) !==
            Number(period.monthPaymentReal);
        const dateChanged =
          buffered.dateDebut &&
          buffered.dateDebut !== formatDate(period.dBegin);
        if (!mensualiteChanged && !dateChanged) continue;

        const ok = await onSaisirMensualiteRetenue(period.num, {
          mensualiteRetenue: buffered.mensualiteRetenue,
          dateDebut: dateChanged ? buffered.dateDebut : undefined,
        });
        if (!ok) return; // erreur déjà notifiée par le hook — on n'écrase pas l'édition en cours
      }
      cancelEcheancierEdition();
    } finally {
      setIsSavingEcheancier(false);
    }
  };

  const gridClass = showEcheancierColumns ? styles.withEcheancier : "";

  return (
    <div>
      {/* LS-612 — bascule Saisir / Modifier / Enregistrer l'échéancier réel.
          LS-724 — la barre reste visible sans droit de saisie dès qu'un palier
          est saisi, pour le tableau d'amortissement seul. */}
      {(canSaisirEcheancier || hasEcheancierValues) && (
        <div className={styles.echeancierBar}>
          {/* LS-612 — vue lecture seule du tableau d'amortissement complet,
              visible dès qu'au moins un palier a été saisi. */}
          {hasEcheancierValues && (
            <button
              className={styles.echeancierRecapBtn}
              onClick={() => setShowEcheancierRecap(true)}
            >
              <i className="fa fa-table" style={{ marginRight: 4 }} />
              Afficher le tableau d&apos;amortissement
            </button>
          )}
          {!canSaisirEcheancier ? null : echeancierEnEdition ? (
            <>
              <button
                className={styles.echeancierSaveBtn}
                onClick={saveEcheancier}
                disabled={isSavingEcheancier}
              >
                <i className="fa fa-check" style={{ marginRight: 4 }} />
                Enregistrer
              </button>
              <button
                className={styles.echeancierCancelBtn}
                onClick={cancelEcheancierEdition}
                disabled={isSavingEcheancier}
              >
                Annuler
              </button>
            </>
          ) : (
            <>
              {hasUnsavedPeriodChange && (
                <span className={styles.echeancierWarning}>
                  <i
                    className="fa fa-exclamation-triangle"
                    style={{ marginRight: 4 }}
                  />
                  Enregistrez d&apos;abord les périodes modifiées
                </span>
              )}
              <button
                className={styles.echeancierToggleBtn}
                onClick={startEcheancierEdition}
                disabled={hasUnsavedPeriodChange}
                title={
                  hasUnsavedPeriodChange
                    ? "Cliquez sur « Enregistrer » (bouton principal) avant de saisir l'échéancier : des périodes ont été ajoutées ou supprimées."
                    : undefined
                }
              >
                <i className="fa fa-pen" style={{ marginRight: 4 }} />
                {hasEcheancierValues
                  ? "Modifier l'échéancier"
                  : "Saisir l'échéancier"}
              </button>
            </>
          )}
        </div>
      )}

      {/* LS-634 — dépôt du calendrier des loyers (Excel source) pour ce devis,
          indépendant de la saisie manuelle de l'échéancier ci-dessus. */}
      <EcheancierUploader
        demandeUuid={demandeUuid}
        devisUuid={quotation.quotation}
        disabled={documentUploadDisabled}
        onUploaded={onDocumentUploaded}
        devisOptions={devisOptions}
        devisLinkedTypeOptions={devisLinkedTypeOptions}
        reloadKey={documentsReloadKey}
      />

      {/* Header + lignes dans un même wrapper scrollable : sous le plancher de
          largeur du mode échéancier (12 colonnes), le tableau scrolle
          horizontalement d'un bloc au lieu d'écraser les champs. */}
      <div className={styles.periodTableScroll}>
        {/* Table header */}
        <div className={`${styles.periodTableHeader} ${gridClass}`}>
          <div>#</div>
          <div>Début</div>
          <div>Durée</div>
          <div>Mens. HT</div>
          <div>Taux devis</div>
          <div>VR TTC</div>
          {showEcheancierColumns && (
            <>
              <div>Mens. retenue HT</div>
              <div>Taux réel</div>
              <div>Début réel</div>
              <div>Écart €</div>
              <div>Montant à échoir</div>
            </>
          )}
          <div></div>
        </div>

        {/* Period rows */}
        <div className={isCalculating ? styles.calculatingOverlay : ""}>
          {quotation.periods.map((period, k) => {
            const buffered = echeancierBuffer[period.num];
            // LS-673 — la VR d'un palier est conservée côté API (jamais écrasée par
            // la grille constructeur) : on affiche la valeur saisie si elle existe,
            // sinon la VR conservée renvoyée par l'API (en euros). Aucun calcul
            // front : l'équivalent € d'une saisie en % vient de `period.vr`.
            // Les saisies % historiques (antérieures au retrait du sélecteur
            // d'unité) affichent leur équivalent € — le taux reste visible dans
            // le champ % dédié en dessous.
            const vrValeurAffichee =
              period.vrUnite === "POURCENTAGE"
                ? (period.vr ?? "")
                : (period.vrValeurSaisie ?? period.vr ?? "");

            return (
              <div
                className={`${styles.periodTableRow} ${gridClass}`}
                key={"pr-" + k}
              >
                <div className={styles.periodRowNum}>{period.num}</div>

                {/* Date début — editable */}
                <div>
                  <input
                    type="date"
                    className={styles.periodInputEditable}
                    name="dBegin"
                    value={formatDate(period.dBegin)}
                    onChange={(e) => handleChange(k, e)}
                    disabled={disabled}
                  />
                </div>

                {/* Durée — editable */}
                <div>
                  <input
                    type="number"
                    className={styles.periodInputEditable}
                    name="duration"
                    value={period.duration}
                    onChange={(e) => handleChange(k, e)}
                    disabled={disabled}
                    min="0"
                  />
                </div>

                {/* Mensualité — calculated */}
                <div>
                  <div className={styles.periodInputCalculated}>
                    {period.monthPayment &&
                    Number(period.monthPayment) !== 0 ? (
                      `${Number(period.monthPayment).toLocaleString("fr-FR")} €`
                    ) : (
                      <span className={styles.periodPlaceholder}>—</span>
                    )}
                  </div>
                </div>

                {/* LS-643 — taux de financement du devis (constant, répété par ligne) */}
                <div>
                  <div className={styles.periodInputCalculated}>
                    {quotation.tauxFinancementDevis != null ? (
                      `${Number(quotation.tauxFinancementDevis).toLocaleString(
                        "fr-FR",
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                      )} %`
                    ) : (
                      <span className={styles.periodPlaceholder}>—</span>
                    )}
                  </div>
                </div>

                {/* LS-673 — VR saisissable manuellement sur TOUS les paliers, premier
                  inclus. Montant exprimé en euros uniquement : le sélecteur €/% a
                  été retiré car, combiné au recalcul automatique déclenché champ
                  par champ, il créait une course (la valeur partait en € avant le
                  basculement d'unité, ou l'unité seule était renvoyée à zéro par
                  l'API faute de valeur). La saisie d'un pourcentage passe par le
                  champ taux dédié ci-dessous (LS-670, même base de calcul). La
                  cascade sur les paliers suivants est calculée côté API, jamais ici. */}
                <div>
                  <div className={styles.periodVrGroup}>
                    <input
                      type="number"
                      className={styles.periodInputEditable}
                      name="vrValeurSaisie"
                      value={vrValeurAffichee}
                      onChange={(e) => {
                        const { value } = e.target;
                        // Une saisie de montant réelle doit reprendre la main sur un
                        // taux manuel précédent : sinon son affichage reste figé sur
                        // l'ancien taux manuel au lieu du taux effectif fraîchement
                        // recalculé depuis ce montant (la VR, elle, est déjà correcte
                        // dans les deux cas — vrUnite/vrValeurSaisie priment toujours
                        // sur tauxVrManuel côté ResoudreVR). Émis avant le champ montant
                        // lui-même pour que la dernière frappe reste celle qui arme le
                        // recalcul au blur (cf. BLUR_RECALC_FIELDS).
                        if (value !== "") {
                          onFieldChange(quotationIndex, k, "tauxVrManuel", "");
                        }
                        onFieldChange(
                          quotationIndex,
                          k,
                          "vrValeurSaisie",
                          value,
                        );
                        // L'API n'accepte une saisie que par couple complet
                        // (valeur + unité) : l'unité € part dans la même frappe.
                        // Champ vidé : on n'émet pas l'unité, pour laisser agir le
                        // garde-fou qui annule le recalcul sur valeur vide.
                        if (value !== "") {
                          onFieldChange(quotationIndex, k, "vrUnite", "EURO");
                        }
                      }}
                      onBlur={() => onFieldCommit?.(quotationIndex, k)}
                      disabled={disabled}
                      min="0"
                      title="Montant en € — pour saisir un pourcentage, utilisez le champ taux ci-dessous"
                    />
                    {" €"}
                  </div>
                  {/* LS-664 — équivalent HT de la VR ci-dessus, dérivé côté API,
                    affiché quel que soit le type de palier (premier, intermédiaire,
                    dernier). */}
                  {period.vrHT != null && (
                    <div className={styles.periodVrResolved}>
                      HT : {Number(period.vrHT).toLocaleString("fr-FR")} €
                    </div>
                  )}
                  {/* LS-670 — taux VR effectif retenu et son origine (devis / grille /
                    saisi / défaut), éditable par ligne : la saisie est exprimée en %
                    et convertie en fraction avant envoi (tauxVrManuel, l'API valide
                    between:0,1). Pilote directement la VR dès le prochain recalcul
                    (priorité 2 de ResoudreVR), y compris le taux repris du devis sur
                    la période 1. */}
                  <div className={styles.periodVrResolved}>
                    <input
                      type="number"
                      className={styles.periodInputEditable}
                      name="tauxVrManuel"
                      value={
                        period.tauxVrManuel != null
                          ? formatTauxPourcentage(period.tauxVrManuel)
                          : formatTauxPourcentage(period.tauxVrEffectif)
                      }
                      onChange={(e) => {
                        const { value } = e.target;
                        // Une saisie de taux réelle doit reprendre la main sur une VR
                        // saisie précédente : sans ce nettoyage, vrUnite/vrValeurSaisie
                        // restent renseignés en priorité 1 de ResoudreVR et ce taux n'a
                        // jamais d'effet sur la VR calculée. Émis avant le champ taux
                        // lui-même pour que ce dernier reste le dernier appel (celui qui
                        // arme le recalcul au blur, cf. BLUR_RECALC_FIELDS).
                        if (value !== "") {
                          onFieldChange(
                            quotationIndex,
                            k,
                            "vrValeurSaisie",
                            "",
                          );
                          onFieldChange(quotationIndex, k, "vrUnite", "");
                        }
                        onFieldChange(
                          quotationIndex,
                          k,
                          "tauxVrManuel",
                          value === "" ? "" : String(Number(value) / 100),
                        );
                      }}
                      onBlur={() => onFieldCommit?.(quotationIndex, k)}
                      disabled={disabled}
                      min="0"
                      max="100"
                      step="0.01"
                      title="Taux VR (% du prix public TTC + options incluses VR)"
                    />
                    {" %"}
                    {period.origineTauxVr && (
                      <>
                        {" "}
                        (
                        {ORIGINE_TAUX_LABELS[period.origineTauxVr] ||
                          period.origineTauxVr}
                        )
                      </>
                    )}
                  </div>
                </div>

                {/* LS-612 — Mens. retenue / Taux réel / Début réel / Écart € / Montant à échoir */}
                {showEcheancierColumns && (
                  <>
                    <div>
                      <input
                        type="number"
                        className={styles.periodInputEditable}
                        value={
                          echeancierEnEdition
                            ? (buffered?.mensualiteRetenue ?? "")
                            : (period.monthPaymentReal ?? "")
                        }
                        onChange={(e) =>
                          handleEcheancierChange(
                            period.num,
                            "mensualiteRetenue",
                            e.target.value,
                          )
                        }
                        disabled={!echeancierEnEdition}
                        min="0"
                      />
                    </div>
                    {/* LS-643 — taux réel implicite des mensualités retenues (calcul API) */}
                    <div>
                      <div className={styles.periodInputCalculated}>
                        {period.tauxReel != null ? (
                          `${Number(period.tauxReel).toLocaleString("fr-FR", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} %`
                        ) : (
                          <span className={styles.periodPlaceholder}>—</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <input
                        type="date"
                        className={styles.periodInputEditable}
                        value={
                          echeancierEnEdition
                            ? (buffered?.dateDebut ?? "")
                            : formatDate(period.dBegin)
                        }
                        onChange={(e) =>
                          handleEcheancierChange(
                            period.num,
                            "dateDebut",
                            e.target.value,
                          )
                        }
                        disabled={!echeancierEnEdition}
                      />
                    </div>
                    <div>
                      {period.ecartEuros != null ? (
                        <span
                          className={
                            period.ecartEuros > 0
                              ? styles.ecartNegatif
                              : styles.ecartPositif
                          }
                        >
                          {period.ecartEuros > 0 ? "+" : ""}
                          {period.ecartEuros.toLocaleString("fr-FR", {
                            maximumFractionDigits: 2,
                          })}{" "}
                          €
                        </span>
                      ) : (
                        <span className={styles.periodPlaceholder}>—</span>
                      )}
                    </div>
                    <div>
                      <div className={styles.periodInputCalculated}>
                        {period.montantAEchoir != null ? (
                          `${Number(period.montantAEchoir).toLocaleString("fr-FR")} €`
                        ) : (
                          <span className={styles.periodPlaceholder}>—</span>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Delete button */}
                <div>
                  {!disabled && quotation.periods.length > 1 && (
                    <button
                      className={styles.periodDeleteBtn}
                      onClick={() => onRemovePeriod(quotationIndex, k)}
                      disabled={echeancierEnEdition}
                      title={
                        echeancierEnEdition
                          ? "Terminez ou annulez la saisie de l'échéancier avant de modifier les périodes"
                          : "Supprimer cette période"
                      }
                    >
                      <i className="fa fa-times" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add period button */}
      {!disabled && (
        <div className={styles.periodAddRow}>
          <button
            className={styles.periodAddBtn}
            onClick={() => onAddPeriod(quotationIndex)}
            disabled={isCalculating || echeancierEnEdition}
            title={
              echeancierEnEdition
                ? "Terminez ou annulez la saisie de l'échéancier avant de modifier les périodes"
                : undefined
            }
          >
            <i className="fa fa-plus" style={{ marginRight: 4 }} />
            Ajouter une période
          </button>
        </div>
      )}

      {showEcheancierRecap && (
        <EcheancierRecapModal
          reference={quotation.reference}
          demandeUuid={demandeUuid}
          devisUuid={quotation.quotation}
          onClose={() => setShowEcheancierRecap(false)}
        />
      )}
    </div>
  );
};

export default FinanceTabV2;
